import { useEffect, useState } from "react";
import { Autocomplete, TextField, Button } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "api/Api";
import "assets/theme/admin_form.css";

const AddWard = () => {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    district_id: "",
    subdistrict_id: "",
    city_id: "",
    ward_number: "",
    ward_name: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch all districts on mount
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await fetch(`${BASE_URL}super_admin/list_district`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await res.json();
        setDistricts(data.districts || data);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchDistricts();
  }, []);

  // Fetch subdistricts when district_id changes
  useEffect(() => {
    if (!formData.district_id) {
      setSubdistricts([]);
      setCities([]);
      return;
    }

    setLoading(true);
    fetch(`${BASE_URL}super_admin/get_all_subdistrict?district_id=${formData.district_id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setSubdistricts(data.subdistricts || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [formData.district_id]);

  // Fetch cities when subdistrict_id changes
  useEffect(() => {
    if (!formData.subdistrict_id) {
      setCities([]);
      return;
    }

    setLoading(true);
    fetch(
      `${BASE_URL}super_admin/cities_by_subdistrict?subdistrict_id=${formData.subdistrict_id}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setCities(data.cities || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [formData.subdistrict_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.district_id) {
      newErrors.district_id = "Please select a district";
    }
    if (!formData.subdistrict_id) {
      newErrors.subdistrict_id = "Please select a subdistrict";
    }
    if (!formData.city_id) {
      newErrors.city_id = "Please select a city";
    }
    if (!formData.ward_number.trim()) {
      newErrors.ward_number = "Ward number is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const res = await fetch(`${BASE_URL}super_admin/add_ward`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      alert("Ward added successfully");
      navigate("/ward-list");
    } catch (error) {
      alert(error.message);
    }
  };

  if (loading && districts.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="p-4 textAlign-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid mt-4" style={{ fontSize: "0.9rem" }}>
        {/* Header Card */}
        <div className="admin-form-header-card">
          <div className="d-flex align-items-center gap-3">
            <button type="button" onClick={() => navigate(-1)} className="btn-admin-back">
              <FaArrowLeft />
              <span>मागे जा (Back)</span>
            </button>
            <div>
              <h4 className="fw-bold mb-1 text-white">
                🏢 नवीन प्रभाग जोडा (Add Ward)
              </h4>
              <small className="text-white-50">District → Subdistrict → City → Ward management</small>
            </div>
          </div>

          <Link to="/ward-list" className="btn btn-light btn-sm fw-semibold rounded-pill px-3">
            Ward List
          </Link>
        </div>

        {/* Main Card */}
        <div className="card shadow-lg rounded-4 border-0">
          <div className="card-body p-4">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-4">
                {/* 1. District Search */}
                <div className="col-md-4">
                  <Autocomplete
                    options={districts}
                    getOptionLabel={(option) => option.district_name || ""}
                    value={
                      districts.find(
                        (d) => d.district_id === formData.district_id
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        district_id: newValue ? newValue.district_id : "",
                        subdistrict_id: "",
                        city_id: "",
                      }));
                      if (errors.district_id) {
                        setErrors((prev) => ({ ...prev, district_id: "" }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Search District <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        fullWidth
                        error={!!errors.district_id}
                        helperText={errors.district_id}
                      />
                    )}
                  />
                </div>

                {/* 2. Subdistrict Search */}
                <div className="col-md-4">
                  <Autocomplete
                    options={subdistricts}
                    getOptionLabel={(option) => option.subdistrict_name || ""}
                    value={
                      subdistricts.find(
                        (sd) => sd.subdistrict_id === formData.subdistrict_id
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        subdistrict_id: newValue ? newValue.subdistrict_id : "",
                        city_id: "",
                      }));
                      if (errors.subdistrict_id) {
                        setErrors((prev) => ({ ...prev, subdistrict_id: "" }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Search Subdistrict <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        fullWidth
                        disabled={!formData.district_id}
                        error={!!errors.subdistrict_id}
                        helperText={errors.subdistrict_id}
                      />
                    )}
                  />
                </div>

                {/* 3. City Search */}
                <div className="col-md-4">
                  <Autocomplete
                    options={cities}
                    getOptionLabel={(option) => option.city_name || ""}
                    value={
                      cities.find((city) => city.city_id === formData.city_id) || null
                    }
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        city_id: newValue ? newValue.city_id : "",
                      }));
                      if (errors.city_id) {
                        setErrors((prev) => ({ ...prev, city_id: "" }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Search City <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        fullWidth
                        disabled={!formData.subdistrict_id}
                        error={!!errors.city_id}
                        helperText={errors.city_id}
                      />
                    )}
                  />
                </div>

                {/* 4. Ward Number */}
                <div className="col-md-6">
                  <TextField
                    label={
                      <span>
                        Ward Number <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    name="ward_number"
                    fullWidth
                    placeholder="e.g. 1, 12A, Ward-05"
                    value={formData.ward_number}
                    onChange={handleChange}
                    error={!!errors.ward_number}
                    helperText={errors.ward_number}
                  />
                </div>

                {/* 5. Ward Name */}
                <div className="col-md-6">
                  <TextField
                    label="Ward Name (Optional)"
                    name="ward_name"
                    fullWidth
                    placeholder="e.g. Shivaji Nagar"
                    value={formData.ward_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-end mt-4">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    fontSize: "0.85rem",
                    px: 5,
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  Save Ward
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddWard;
